/**
 * SuperAdminDashboard Component
 * Main dashboard for super admin with user and event management tabs
 * Feature: User Management (002-user-management)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import ApiService from '../services/ApiService';
import AuthService from '../services/AuthService';
import SystemSettings from './SystemSettings';
import CategoryManager from './CategoryManager';
import AgendaWizardSettings from './AgendaWizardSettings';
import UserMenu from './UserMenu';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [userForm, setUserForm] = useState({ email: '', role: 'admin', segment: 'OPS-SW' });
  const ALL_SEGMENTS = ['OPS_SW', 'GROW_PLM', 'LCS', 'ME', 'PES', 'SIM'];
  const SEGMENT_LABELS = { OPS_SW: 'OPS-SW', GROW_PLM: 'Grow@PLM', LCS: 'LCS', ME: 'ME', PES: 'PES', SIM: 'SIM' };
  const [eventForm, setEventForm] = useState({ eventName: '', eventDate: '', status: 'active', enabledSegments: [...ALL_SEGMENTS] });

  // Change password modal state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.newPass !== passwordForm.confirm) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (passwordForm.newPass.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    setPasswordLoading(true);
    try {
      await AuthService.changePassword(passwordForm.current, passwordForm.newPass);
      setPasswordForm({ current: '', newPass: '', confirm: '' });
      setShowChangePassword(false);
      alert('Password changed successfully.');
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'events') {
      loadEvents();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ApiService.get('/super-admin/dashboard');
      setStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ApiService.get('/super-admin/users');
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ApiService.get('/super-admin/events');
      setEvents(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        email: userForm.email.trim(),
        role: userForm.role,
        segment: userForm.role === 'admin' ? userForm.segment : null
      };
      await ApiService.post('/super-admin/users', payload);
      setShowUserForm(false);
      setUserForm({ email: '', role: 'admin', segment: 'OPS-SW' });
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        email: userForm.email.trim(),
        role: userForm.role,
        segment: userForm.role === 'admin' ? userForm.segment : null
      };
      await ApiService.put(`/super-admin/users/${editingItem}`, payload);
      setEditingItem(null);
      setUserForm({ email: '', role: 'admin', segment: 'OPS-SW' });
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    setError('');
    try {
      await ApiService.delete(`/super-admin/users/${id}`);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await ApiService.post('/super-admin/events', eventForm);
      setShowEventForm(false);
      setEventForm({ eventName: '', eventDate: '', status: 'active', enabledSegments: [...ALL_SEGMENTS] });
      loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await ApiService.put(`/super-admin/events/${editingItem}`, eventForm);
      setEditingItem(null);
      setEventForm({ eventName: '', eventDate: '', status: 'active', enabledSegments: [...ALL_SEGMENTS] });
      loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleArchiveEvent = async (id) => {
    if (!confirm('Archive this event?')) return;
    setError('');
    try {
      await ApiService.post(`/super-admin/events/${id}/archive`, {});
      loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!confirm('Delete this event? This is only allowed for events with no sessions.')) return;
    setError('');
    try {
      await ApiService.delete(`/super-admin/events/${id}`);
      loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEditUser = (userItem) => {
    setEditingItem(userItem.id);
    setUserForm({
      role: userItem.role,
      segment: userItem.segment || 'OPS-SW',
      email: userItem.email || userItem.username || ''
    });
  };

  const startEditEvent = (event) => {
    setEditingItem(event.id);
    setEventForm({
      eventName: event.eventName,
      eventDate: event.eventDate.split('T')[0],
      status: event.status,
      enabledSegments: event.enabledSegments || [...ALL_SEGMENTS]
    });
  };

  return (
    <div className="super-admin-dashboard">
      <header className="dashboard-header">
        <h1>Super Admin Dashboard</h1>
        <div className="header-info" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>Welcome, {user?.name || user?.username}</span>
          {user?.authType !== 'entra' && (
            <button onClick={() => { setShowChangePassword(true); setPasswordError(''); setPasswordSuccess(''); }} className="logout-btn">Change Password</button>
          )}
          <button onClick={logout} className="logout-btn">Logout</button>
          <UserMenu />
        </div>
      </header>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', padding: '32px', width: '400px', maxWidth: '90vw', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '20px', color: '#111827' }}>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Current Password</label>
                <input type="password" value={passwordForm.current} onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>New Password</label>
                <input type="password" value={passwordForm.newPass} onChange={e => setPasswordForm({ ...passwordForm, newPass: e.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>Confirm New Password</label>
                <input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })} required style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              {passwordError && <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '16px', marginTop: 0 }}>{passwordError}</p>}
              {passwordSuccess && <p style={{ color: '#059669', fontSize: '14px', marginBottom: '16px', marginTop: 0 }}>{passwordSuccess}</p>}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowChangePassword(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Cancel</button>
                <button type="submit" disabled={passwordLoading} style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: '#005f87', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>{passwordLoading ? 'Saving...' : 'Change Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <nav className="dashboard-tabs">
        <button
          className={activeTab === 'dashboard' ? 'tab-active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={activeTab === 'users' ? 'tab-active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={activeTab === 'events' ? 'tab-active' : ''}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={activeTab === 'categories' ? 'tab-active' : ''}
          onClick={() => setActiveTab('categories')}
        >
          🏷️ Categories
        </button>
        <button
          className={activeTab === 'settings' ? 'tab-active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
        <button
          className={activeTab === 'agenda-wizard' ? 'tab-active' : ''}
          onClick={() => setActiveTab('agenda-wizard')}
        >
          🪄 Agenda Wizard
        </button>
        <button
          className={activeTab === 'urls' ? 'tab-active' : ''}
          onClick={() => setActiveTab('urls')}
        >
          🔗 URLs
        </button>
        <button
          className="tab-external"
          onClick={() => navigate('/admin/legacy')}
        >
          📤 Session Upload
        </button>
        <button
          className="tab-external tab-health"
          onClick={() => navigate('/health')}
        >
          🏥 Health
        </button>
      </nav>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-content">
        {activeTab === 'dashboard' && stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Users</h3>
              <p className="stat-number">{stats.users.total}</p>
              <p className="stat-detail">Super Admins: {stats.users.superAdmins} | Admins: {stats.users.admins}</p>
            </div>
            <div className="stat-card">
              <h3>Events</h3>
              <p className="stat-number">{stats.events.total}</p>
              <p className="stat-detail">Active: {stats.events.active} | Archived: {stats.events.archived}</p>
            </div>
            <div className="stat-card">
              <h3>Sessions</h3>
              <p className="stat-number">{stats.events.totalSessions}</p>
              <p className="stat-detail">Across all events</p>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div className="section-header">
              <h2>User Management</h2>
              <button onClick={() => { setShowUserForm(true); setEditingItem(null); setUserForm({ email: '', role: 'admin', segment: 'OPS-SW' }); }} className="btn-primary">
                Add User
              </button>
            </div>

            {(showUserForm || editingItem) && (
              <form onSubmit={editingItem ? handleUpdateUser : handleCreateUser} className="item-form">
                <input
                  type="email"
                  placeholder="Siemens Email (e.g. user@siemens.com)"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required
                />
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  required
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
                {userForm.role === 'admin' && (
                  <select
                    value={userForm.segment}
                    onChange={(e) => setUserForm({ ...userForm, segment: e.target.value })}
                    required
                  >
                    <option value="OPS-SW">OPS-SW</option>
                    <option value="Grow@PLM">Grow@PLM</option>
                    <option value="LCS">LCS</option>
                    <option value="ME">ME</option>
                    <option value="PES">PES</option>
                    <option value="SIM">SIM</option>
                  </select>
                )}
                <div className="form-actions">
                  <button type="submit" className="btn-primary">{editingItem ? 'Update User' : 'Add User'}</button>
                  <button type="button" onClick={() => { setShowUserForm(false); setEditingItem(null); }} className="btn-secondary">Cancel</button>
                </div>
              </form>
            )}

            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Segment</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email || u.username}</td>
                    <td>{u.displayName || '-'}</td>
                    <td>
                      <span className={`role-tag role-${u.role}`}>
                        {u.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td>{u.segment || '-'}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => startEditUser(u)} className="btn-sm">Edit</button>
                      <button onClick={() => handleDeleteUser(u.id)} className="btn-sm btn-danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'events' && (
          <div>
            <div className="section-header">
              <h2>Event Management</h2>
              <button onClick={() => setShowEventForm(true)} className="btn-primary">
                Create Event
              </button>
            </div>

            {(showEventForm || editingItem) && (
              <form onSubmit={editingItem ? handleUpdateEvent : handleCreateEvent} className="item-form">
                <input
                  type="text"
                  placeholder="Event Name (e.g., 27-Jan-2026 Learning Day)"
                  value={eventForm.eventName}
                  onChange={(e) => setEventForm({ ...eventForm, eventName: e.target.value })}
                  required
                />
                <input
                  type="date"
                  value={eventForm.eventDate}
                  onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })}
                  required
                />
                <select
                  value={eventForm.status}
                  onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
                  required
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
                <div className="segment-checkboxes">
                  <label className="segment-checkboxes-label">Enabled Segments:</label>
                  <div className="segment-checkboxes-row">
                    {ALL_SEGMENTS.map(seg => (
                      <label key={seg} className="segment-checkbox-item">
                        <input
                          type="checkbox"
                          checked={eventForm.enabledSegments.includes(seg)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...eventForm.enabledSegments, seg]
                              : eventForm.enabledSegments.filter(s => s !== seg);
                            setEventForm({ ...eventForm, enabledSegments: next });
                          }}
                        />
                        {SEGMENT_LABELS[seg] || seg}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">{editingItem ? 'Update' : 'Create'}</button>
                  <button type="button" onClick={() => { setShowEventForm(false); setEditingItem(null); }} className="btn-secondary">Cancel</button>
                </div>
              </form>
            )}

            <table className="data-table">
              <thead>
                <tr>
                  <th>Event Name</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Segments</th>
                  <th>Sessions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                      No events found. Create your first event using the form above.
                    </td>
                  </tr>
                ) : (
                  events.map((evt) => (
                    <tr key={evt.id}>
                      <td>{evt.eventName}</td>
                      <td>{new Date(evt.eventDate).toLocaleDateString()}</td>
                      <td><span className={`status-badge status-${evt.status}`}>{evt.status}</span></td>
                      <td>
                        <div className="segment-badges">
                          {(evt.enabledSegments || ALL_SEGMENTS).map(seg => (
                            <span key={seg} className="segment-badge">{SEGMENT_LABELS[seg] || seg}</span>
                          ))}
                        </div>
                      </td>
                      <td>{evt.sessionCount || 0}</td>
                      <td>
                        <button onClick={() => startEditEvent(evt)} className="btn-sm">Edit</button>
                        {evt.status === 'active' && (
                          <button onClick={() => handleArchiveEvent(evt.id)} className="btn-sm">Archive</button>
                        )}
                        <button onClick={() => handleDeleteEvent(evt.id)} className="btn-sm btn-danger">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'categories' && (
          <CategoryManager />
        )}

        {activeTab === 'settings' && (
          <SystemSettings />
        )}

        {activeTab === 'agenda-wizard' && (
          <AgendaWizardSettings />
        )}

        {activeTab === 'urls' && (() => {
          const base = window.location.origin;
          const segmentUrls = [
            { code: 'OPS-SW', label: 'OPS-SW', path: '/ops-sw' },
            { code: 'GROW_PLM', label: 'Grow@PLM', path: '/grow-plm' },
            { code: 'LCS', label: 'LCS', path: '/lcs' },
            { code: 'ME', label: 'ME', path: '/me' },
            { code: 'PES', label: 'PES', path: '/pes' },
            { code: 'SIM', label: 'SIM', path: '/sim' },
          ];
          return (
            <div>
              <div className="section-header">
                <h2>Segment URLs</h2>
              </div>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '14px' }}>
                Share these URLs with segment users. Each URL opens the Learning Day homepage with the segment tab pre-selected.
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th>URL</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {segmentUrls.map(({ code, label, path }) => {
                    const fullUrl = `${base}${path}`;
                    return (
                      <tr key={code}>
                        <td><strong>{label}</strong></td>
                        <td>
                          <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#005f87', fontFamily: 'monospace', fontSize: '14px' }}>
                            {fullUrl}
                          </a>
                        </td>
                        <td>
                          <button
                            className="btn-sm"
                            onClick={() => {
                              navigator.clipboard.writeText(fullUrl);
                            }}
                          >
                            📋 Copy
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}

        {loading && <div className="loading-spinner">Loading...</div>}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
